/**
 * BALANCE.js - World Map Balance Constants
 */

// ========================================
// EXPLORATION LIMITS
// ========================================
export const EXPLORATION = {
  baseLimit: 60,  // Can discover 60 tiles without any tech (doubled for testing)

  // First N tiles guaranteed to include essential resources
  starterProtection: {
    tilesProtected: 15,  // First 15 tiles use starter pool

    // GUARANTEED: By tile 15, player MUST have access to wood, stone, food, knowledge, and gold
    guaranteedResources: {
      wood: ['forest', 'swamp'],           // Biomes that give wood
      stone: ['mountains', 'rockyOutcrop'], // Biomes that give stone
      food: ['plains', 'grassland', 'forest', 'swamp'], // Biomes that give food
      knowledge: ['overgrownShrine'],       // Biomes that give knowledge (early game)
      gold: ['leprechaunHollow'],           // Biomes that give gold (T1 gold production)
    },

    // Check at this tile count - if missing resource, force spawn it
    guaranteeCheckpoint: 10, // At tile 10+, start forcing missing resources

    // Normal starter weights (when not forcing)
    starterBiomes: ['forest', 'plains', 'mountains', 'grassland', 'rockyOutcrop', 'swamp', 'overgrownShrine', 'leprechaunHollow'],
    starterWeights: {
      forest: 30,           // Wood + food
      plains: 25,           // Food
      mountains: 20,        // Stone + iron
      grassland: 15,        // Food
      rockyOutcrop: 5,      // Stone
      swamp: 5,             // Wood + food
      overgrownShrine: 12,  // Knowledge (early access for progression)
      leprechaunHollow: 8,  // Gold (early economy boost)
    },
  },

  // Guaranteed dungeon spawn system
  // Ensures at least 1 dungeon of your current tier spawns within N tiles
  dungeonGuarantee: {
    enabled: true,
    tilesPerGuarantee: 15,  // Guarantee 1 age-appropriate dungeon every 15 tiles

    // Which dungeon biomes match which ages
    // Keys = minAge from dungeon biomes, values = dungeon biome IDs
    dungeonByAge: {
      1: ['ancientRuins'],       // Age 1-2: Ancient Ruins
      3: ['darkCatacombs'],      // Age 3-4: Dark Catacombs
      5: ['corruptedTemple'],    // Age 5+: Corrupted Temple
    },
  },

  // Guaranteed tower spawn system
  // Ensures at least 1 tower of your current tier spawns within N tiles
  towerGuarantee: {
    enabled: true,
    tilesPerGuarantee: 20,  // Guarantee 1 age-appropriate tower every 20 tiles

    // Which tower biomes match which ages
    // Keys = minAge from tower biomes, values = tower biome IDs
    towerByAge: {
      1: ['abandonedTower'],     // Age 1-2: Abandoned Tower (3 floors)
      3: ['mageSpire'],          // Age 3-4: Mage Spire (5 floors)
      5: ['darkCitadel'],        // Age 5-6: Dark Citadel (5 floors)
      7: ['voidTower'],          // Age 7-8: Void Tower (10 floors)
      9: ['eternalSpire'],       // Age 9+: Eternal Spire (10 floors)
    },
  },
  
  // Tech bonuses - keys must match your technology IDs
  techBonuses: {
    // Early game
    'mining': 30,
    'construction': 30,
    'agriculture': 30,

    // Mid game
    'commerce': 60,
    'sailing': 90,        // Big bonus - sea navigation
    'engineering': 60,

    // Late game
    'astronomy': 90,      // Star navigation
    'scientific_method': 60,

    // End game
    'arcane_arts': 120,
    'ley_lines': 300,     // Major expansion - dimensional sight
  },
  
  // Some biomes require specific techs to even discover
  biomeRequirements: {
    'shallowWaters': 'sailing',
    'deepOcean': 'sailing',
    'ancientRuins': 'archaeology',
    'voidRift': 'arcane_arts',
    'mysticGrove': 'mysticism',
  },
};

export const MOVEMENT = {
  exploredCooldown: 3000,    // 3s cooldown for moving to already-explored tiles
  unexploredCooldown: 10000, // 10s cooldown for discovering new tiles
  techBonuses: {
    cartography: 0,    // No bonus
    advanced_cartography: 0,
    master_cartography: 0,
  },
  minimumCooldown: 1000,     // 1s minimum cooldown
  returnToKingdomCooldown: 0,
};

export const GATHER = {
  baseCooldown: 0,           // No cooldown for manual gathering (spam clickable)
  autoGatherDelay: 500,      // 0.5s delay for auto-gathering
  baseMultiplier: 1.0,       // Base yield multiplier
  outpostBonus: 0.1,         // +10% yield per outpost level
  ageBonus: 0.05,            // +5% yield per age/tier
};

// ========================================
// EXPEDITION SYSTEM
// ========================================
export const EXPEDITION = {
  // Available expedition durations
  // Multiplier applies to base gather yield
  // Longer expeditions = bigger rewards, but you're locked in place
  durations: [
    { id: 'quick',    label: '30s',   duration: 30000,   multiplier: 20,  expReward: 15,  dropCount: [1, 2] },
    { id: 'short',    label: '1m',    duration: 60000,   multiplier: 40,  expReward: 35,  dropCount: [1, 3] },
    { id: 'medium',   label: '2m',    duration: 120000,  multiplier: 100, expReward: 80,  dropCount: [2, 4] },
    { id: 'long',     label: '5m',    duration: 300000,  multiplier: 320, expReward: 180, dropCount: [3, 5] },
    { id: 'extended', label: '10m',   duration: 600000,  multiplier: 550, expReward: 350, dropCount: [4, 7] },
  ],

  // XP scaling by player level (to prevent abuse at high levels)
  // Formula: expReward * (1 + playerLevel * 0.05) * levelScaleCap * earlyGameBonus
  levelScaleCap: 3.0,  // Max 3x multiplier from level

  // Early game XP bonus (helps new players level faster)
  // 2x XP at level 1, linearly diminishes to 1x at level 10
  earlyGameBonusMaxLevel: 10,  // Bonus ends at this level
  earlyGameBonusMultiplier: 2.0,  // Starting bonus at level 1

  // Expedition bonuses
  criticalSuccessChance: 0.15, // 15% chance to DOUBLE all rewards (resources + XP + drops)

  // Possible drops from expeditions (materials from mobs)
  // Higher tier drops require higher player level
  dropTable: [
    // Basic materials (always available)
    { item: 'bone', weight: 20, minQty: 1, maxQty: 3, minLevel: 1 },
    { item: 'leather', weight: 20, minQty: 1, maxQty: 3, minLevel: 1 },
    { item: 'cloth', weight: 20, minQty: 1, maxQty: 3, minLevel: 1 },
    // Uncommon materials
    { item: 'rustyScrap', weight: 15, minQty: 1, maxQty: 2, minLevel: 5 },
    { item: 'ironOre', weight: 12, minQty: 1, maxQty: 2, minLevel: 8 },
    // Consumables
    { item: 'minorHealthPotion', weight: 10, minQty: 1, maxQty: 2, minLevel: 1 },
    { item: 'healthPotion', weight: 5, minQty: 1, maxQty: 1, minLevel: 10 },
    // Star Essence (rare from expeditions)
    { item: 'starEssence', weight: 8, minQty: 1, maxQty: 3, minLevel: 5 },
    // Gemstones (very rare from expeditions)
    { item: 'ruby', weight: 2, minQty: 1, maxQty: 1, minLevel: 15 },
    { item: 'sapphire', weight: 2, minQty: 1, maxQty: 1, minLevel: 15 },
    { item: 'emerald', weight: 2, minQty: 1, maxQty: 1, minLevel: 15 },
  ],

  // Expedition locks:
  // - Cannot move while on expedition
  // - Cannot teleport while on expedition
  // - Cannot gather while on expedition
  // - Cannot start another expedition

  // Cancel behavior:
  // - Can cancel anytime
  // - NO partial rewards (all or nothing)

  // Reward calculation:
  // expeditionYield = gatherYield × expeditionMultiplier
  // Uses same modifiers as gather: tile quality, outpost bonus, age bonus
  // XP is GUARANTEED based on duration tier
  // Drops are GUARANTEED (1-7 items based on duration)
  // 15% chance to DOUBLE all rewards (critical success)
};

// Helper to get expedition config by ID
export function getExpeditionConfig(durationId) {
  return EXPEDITION.durations.find(d => d.id === durationId) || null;
}

// Helper to format expedition time remaining
export function formatExpeditionTime(ms) {
  if (ms <= 0) return '0s';
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
}

export const BUILDING_CAPS = {
  universalBaseCap: 5,
  outpostBaseCap: 5,
  capacityPerLevel: 5,
  maxOutpostLevel: 10,
};

export const OUTPOST_BUILD_COSTS = {
  forest:     { wood: 100, stone: 50 },
  plains:     { wood: 80, food: 100 },
  mountains:  { wood: 150, stone: 200 },
  coastal:    { wood: 200, gold: 100 },
  ruins:      { stone: 300, knowledge: 100 },
  volcanic:   { stone: 400, iron: 200, coal: 100 },
  mysticGrove: { wood: 500, magic: 100 },
  voidRift:   { stone: 600, magic: 200, energy: 50 },
};

export const OUTPOST_UPGRADE_CONFIG = {
  scalingFactor: 1.8,
  tierScaling: 1.5,
  biomes: {
    forest: {
      base: { wood: 50, stone: 25 },
      tiers: [
        { level: 4, resources: { iron: 20 } },
        { level: 7, resources: { steel: 15 } },
        { level: 9, resources: { magic: 10 } },
      ],
    },
    plains: {
      base: { food: 60, wood: 30 },
      tiers: [
        { level: 4, resources: { gold: 50 } },
        { level: 7, resources: { knowledge: 30 } },
        { level: 9, resources: { magic: 10 } },
      ],
    },
    mountains: {
      base: { stone: 80, iron: 40 },
      tiers: [
        { level: 4, resources: { coal: 30 } },
        { level: 7, resources: { steel: 25 } },
        { level: 9, resources: { crystals: 15 } },
      ],
    },
    coastal: {
      base: { gold: 50, wood: 40 },
      tiers: [
        { level: 4, resources: { stone: 100 } },
        { level: 7, resources: { knowledge: 40 } },
        { level: 9, resources: { magic: 15 } },
      ],
    },
    ruins: {
      base: { knowledge: 40, stone: 60 },
      tiers: [
        { level: 4, resources: { gold: 100 } },
        { level: 7, resources: { magic: 30 } },
        { level: 9, resources: { crystals: 20 } },
      ],
    },
    volcanic: {
      base: { iron: 80, coal: 50 },
      tiers: [
        { level: 4, resources: { steel: 40 } },
        { level: 7, resources: { magic: 35 } },
        { level: 9, resources: { energy: 10 } },
      ],
    },
    mysticGrove: {
      base: { magic: 50, wood: 100 },
      tiers: [
        { level: 4, resources: { crystals: 30 } },
        { level: 7, resources: { knowledge: 80 } },
        { level: 9, resources: { energy: 15 } },
      ],
    },
    voidRift: {
      base: { energy: 30, magic: 60 },
      tiers: [
        { level: 4, resources: { crystals: 50 } },
        { level: 7, resources: { knowledge: 100 } },
        { level: 9, resources: { artifacts: 5 } },
      ],
    },
  },
};

export function calculateUpgradeCost(biomeId, currentLevel) {
  const config = OUTPOST_UPGRADE_CONFIG.biomes[biomeId];
  if (!config) return {};
  
  const { scalingFactor, tierScaling } = OUTPOST_UPGRADE_CONFIG;
  const cost = {};
  
  const baseMultiplier = Math.pow(scalingFactor, currentLevel);
  for (const [resource, baseAmount] of Object.entries(config.base)) {
    cost[resource] = Math.floor(baseAmount * baseMultiplier);
  }
  
  for (const tier of config.tiers) {
    if (currentLevel >= tier.level) {
      const tierMultiplier = Math.pow(tierScaling, currentLevel - tier.level);
      for (const [resource, baseAmount] of Object.entries(tier.resources)) {
        const tierCost = Math.floor(baseAmount * tierMultiplier);
        cost[resource] = (cost[resource] || 0) + tierCost;
      }
    }
  }
  
  return cost;
}

export const GATHERING = {
  cooldown: 3000,
  ageMultipliers: {
    1: 1.0, 2: 1.2, 3: 1.5, 4: 2.0, 5: 2.5,
    6: 3.0, 7: 4.0, 8: 5.0, 9: 7.0, 10: 10.0, 11: 15.0,
  },
  gatherDuration: 1500,
};

export const BIOME_GENERATION = {
  defaultWeights: {
    forest: 30, plains: 30, mountains: 20, coastal: 15,
    ruins: 12, volcanic: 10, mysticGrove: 8, voidRift: 5,
  },
};

// ========================================
// TRADE ROUTE SYSTEM
// ========================================
export const TRADE_ROUTES = {
  // Base cost for building a route segment
  baseCost: {
    wood: 50,
    stone: 30,
    gold: 20,
  },

  // Cost scaling formula: baseCost × (1 + distanceMultiplier × distance)
  distanceMultiplier: 0.15,  // +15% per hex distance from kingdom

  // Maximum cost multiplier cap (prevents runaway at distance 50+)
  maxMultiplier: 10.0,

  // Tech bonuses that reduce cost (applied as: cost × (1 - sum of bonuses))
  techBonuses: {
    'commerce': 0.10,      // -10% route cost
    'engineering': 0.15,   // -15% route cost
  },

  // Minimum cost multiplier after tech bonuses
  minCostMultiplier: 0.5,  // Never less than 50% of base cost
};

export function getBiomeSpawnWeights(currentAge, biomes) {
  // Default to age 1 if undefined or 0
  const age = currentAge || 1;
  const weights = {};
  for (const [id, biome] of Object.entries(biomes)) {
    if (age >= biome.minAge) {
      weights[id] = biome.spawnWeight;
    }
  }
  return weights;
}

export function selectRandomBiome(weights) {
  const entries = Object.entries(weights);
  
  // Fallback if no biomes available (shouldn't happen, but safety first)
  if (entries.length === 0) {
    return 'forest'; // Default fallback biome
  }
  
  const totalWeight = entries.reduce((sum, [, weight]) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  for (const [biomeId, weight] of entries) {
    random -= weight;
    if (random <= 0) return biomeId;
  }
  return entries[0][0];
}

export default {
  EXPLORATION,
  MOVEMENT,
  GATHER,
  EXPEDITION,
  getExpeditionConfig,
  formatExpeditionTime,
  BUILDING_CAPS,
  OUTPOST_BUILD_COSTS,
  OUTPOST_UPGRADE_CONFIG,
  GATHERING,
  BIOME_GENERATION,
  TRADE_ROUTES,
  calculateUpgradeCost,
  getBiomeSpawnWeights,
  selectRandomBiome,
};
